import { forwardRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getCoreRowModel, Table, useReactTable } from "@tanstack/react-table";

import {
  HorizontalBar,
  PieChartIcon,
  TableViewIcon,
  VerticalBarIcon
} from "@/assets/icons";
import TabSelect, { TabItem } from "@/components/tabSelect";
import { Button } from "@/components/ui/button";
import DonutChart, { DonutChartItem } from "@/components/ui/chart/DonutChart";
import HorizontalGroupedBarChart from "@/components/ui/chart/HorizontalGroupedBarChart";
import VerticalBarChart from "@/components/ui/chart/VerticalBarChart";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import InfoBox from "@/components/ui/InfoBox";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { LIMITED_DATA_SUMMARY } from "@/constants/dataset";
import { MESSAGE } from "@/constants/message";
import { useAnalysisDatasetSummary } from "@/hooks/spatialAnalysis/useAnalysisDatasetSummary";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { DatasetData } from "@/types/dataset";

import AggregationPopover from "./aggregationPopover";
import { CATEGORY_OPTIONS } from "./constants";
import TransposedTable from "./transposedTable";
import { ECategoryTransform } from "./types";
import { getDefaultAnalysisConfig, getDefaultViewType } from "./utils";

type DatasetSectionProps = {
  dataset: DatasetData;
  isExpanded?: boolean;
  isLastType?: boolean;
};

export enum DatasetSectionViewType {
  DONUT_CHART = "donutChart",
  BAR_CHART = "barChart",
  TABLE = "table",
  VERTICAL_BAR = "verticalBar"
}

const VIEW_OPTIONS: TabItem[] = [
  {
    label: "",
    value: DatasetSectionViewType.BAR_CHART,
    icon: <HorizontalBar />
  },
  {
    label: "",
    value: DatasetSectionViewType.VERTICAL_BAR,
    icon: <VerticalBarIcon />
  },
  {
    label: "",
    value: DatasetSectionViewType.DONUT_CHART,
    icon: <PieChartIcon />
  },
  {
    label: "",
    value: DatasetSectionViewType.TABLE,
    icon: <TableViewIcon className="w-6" />
  }
];

export type TransposeRow = Record<string, unknown>;

const DatasetSection = forwardRef<HTMLDivElement, DatasetSectionProps>(
  ({ dataset, isLastType, isExpanded }, ref) => {
    const [isAggregationFilterOpen, setIsAggregationFilterOpen] =
      useState(false);
    const { latestAnalysisId } = useSelector(
      (state: RootState) => state.spatial
    );
    const { gridsSelected } = useSelector(
      (state: RootState) => state.analysisSummary
    );

    const {
      isLoadingDatasetSummary,
      columns,
      rows,
      donutChartData,
      barChartData,
      verticalBarChartData,
      filterByOptions,
      analysisConfiguration,
      viewType,
      attributesOptions,
      sortByOptions,
      setAnalysisConfiguration,
      setViewType,
      setFilterByOptions,
      hasNumericColumn,
      datasetSchema
    } = useAnalysisDatasetSummary(dataset, latestAnalysisId, gridsSelected);

    // Table instance
    const table = useReactTable<TransposeRow>({
      data: rows,
      columns: columns,
      getCoreRowModel: getCoreRowModel()
    });

    const handleChangeViewType = (value: string) =>
      setViewType(value as DatasetSectionViewType);

    const handleReset = () => {
      const defaultConfig = getDefaultAnalysisConfig(
        dataset,
        hasNumericColumn,
        attributesOptions
      );
      setAnalysisConfiguration(defaultConfig);
      setViewType(
        getDefaultViewType(dataset, attributesOptions, datasetSchema)
      );
    };

    /*
     * Set default analysis configuration when sortByOptions change
     * This ensures that the category and sortBy are set correctly based on the selected grids
     * If all grids are selected and current category is DISPLAY_COLUMNS => change to DISPLAY_ROW
     */
    useEffect(() => {
      let category = analysisConfiguration?.category;
      if (
        category?.value === ECategoryTransform.DISPLAY_ROWS_SERIES &&
        gridsSelected?.[0] === DROPDOWN_ALL_VALUE
      )
        category = CATEGORY_OPTIONS[0];

      setAnalysisConfiguration((prev) => ({
        ...prev,
        ...(category && { category }),
        sortBy: sortByOptions?.[0]
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortByOptions]);

    return (
      <div
        className={cn(
          "flex flex-col h-fit border border-gainsboro rounded-xl px-4 pt-2 pb-4 mt-3 gap-4 break-inside-avoid",
          isExpanded && "first:mt-0",
          isLastType && "col-span-2 w-1/2"
        )}
        ref={ref}
      >
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-txtHeading">
            {dataset.display_name ?? dataset.name}
          </span>
          <div className="flex flex-row items-center">
            <Button
              className="!no-underline text-sm font-semibold text-teal-500"
              variant="link"
              onClick={handleReset}
            >
              Reset
            </Button>
            <AggregationPopover
              dataset={dataset}
              gridsSelected={gridsSelected}
              isOpen={isAggregationFilterOpen}
              analysisConfiguration={analysisConfiguration}
              onOpenChange={setIsAggregationFilterOpen}
              setAnalysisConfiguration={setAnalysisConfiguration}
              setFilterByOptions={setFilterByOptions}
              attributesOptions={attributesOptions}
              filterByOptions={filterByOptions}
            />
          </div>
        </div>
        {/* View Type */}
        <div className="flex items-center gap-2 print:hidden">
          <span>View Type</span>
          <TabSelect
            className="bg-transparent h-auto gap-2"
            buttonClassName="p-0"
            buttonSelectedClassName="!border-none p-0"
            wrapperListClassName="w-full gap-1"
            tabList={VIEW_OPTIONS}
            selected={viewType}
            onSelect={handleChangeViewType}
          />
        </div>
        {/* View By Type Content */}
        <LoadingWrapper isLoading={isLoadingDatasetSummary}>
          {viewType === DatasetSectionViewType.TABLE && (
            <div className="space-y-3">
              <TransposedTable
                table={table as Table<unknown>}
                limit={LIMITED_DATA_SUMMARY.TABLE}
              />
              {table.getRowModel().rows.length >=
                LIMITED_DATA_SUMMARY.TABLE - 1 && (
                <InfoBox
                  message={
                    MESSAGE.ANALYSIS_SUMMARY_SUMMARY_DATA_TABLE_LIMIT_INFO_10
                  }
                />
              )}
            </div>
          )}
          {viewType === DatasetSectionViewType.VERTICAL_BAR && (
            <VerticalBarChart data={verticalBarChartData} />
          )}
          {viewType === DatasetSectionViewType.DONUT_CHART && (
            <DonutChart data={donutChartData as DonutChartItem[]} />
          )}
          {viewType === DatasetSectionViewType.BAR_CHART && (
            <div>
              <HorizontalGroupedBarChart rawData={barChartData} />
              {barChartData.length === LIMITED_DATA_SUMMARY.BAR_CHART && (
                <InfoBox
                  message={MESSAGE.ANALYSIS_SUMMARY_DATA_BAR_CHART_LIMIT_10}
                />
              )}
            </div>
          )}
        </LoadingWrapper>
      </div>
    );
  }
);

export default DatasetSection;
